import { useCallback, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Button, IconButton, Typography, Box, Tooltip } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import ResponsiveTabs from "@nualang/nualang-ui-components/dist/Navigation/ResponsiveTabs";

import CourseList from "@nualang/nualang-ui-components/dist/Lists/Courses";
import CoursesNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Courses";
import CoursesToolbar from "./CoursesToolbar";
import stickyBarBleed from "../stickyBarBleed";
import { courses as courseHelpers } from "../../../utils/helpers";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  actions: {
    marginBottom: theme.spacing(1),
  },
  header: {
    display: "flex",
    alignItems: "center",
  },
  headerText: {
    flexGrow: 1,
  },
  title: {
    fontWeight: "bold",
    fontSize: "1.2em",
    marginBottom: theme.spacing(2),
  },
}));

// One tab's toolbar + list, with its own independent filters state. 
function CoursesTabPanel({
  t,
  classes,
  courses,
  isLoading,
  showVisibility,
  notFoundTitle,
  notFoundDescription,
  notFoundActionProps,
  placeholderImageUrl,
  completions,
  handleStartCourse,
  handleLeaveCourse,
  handleViewCourse,
  handleViewCourseTopic,
  handleViewUserProfile,
  membersProps,
  getMemberDetails,
  singleLineGrid,
  singleLineGridCols,
  memberPlaceholderImageUrl,
  getCourseSections,
  getCourseMember,
  getCourseMembers,
  memberId,
  username,
  handleDuplicateCourse,
  email,
  isTeacher,
  handleDeleteCourse,
}) {
  const [filters, setFilters] = useState(courseHelpers.DEFAULT_FILTERS);

  const handleVisibilityChange = useCallback((event) => {
    setFilters((prev) => ({ ...prev, visibility: event.target.value }));
  }, []);
  const handleLangChange = useCallback((event) => {
    setFilters((prev) => ({ ...prev, lang: event.target.value }));
  }, []);
  const handleDifficultyChange = useCallback((event) => {
    setFilters((prev) => ({ ...prev, difficulty: event.target.value }));
  }, []);
  const handleSortChange = useCallback((event) => {
    setFilters((prev) => ({ ...prev, sort: event.target.value }));
  }, []);
  const handleSearchChange = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
  }, []);
  const handleClearFilters = useCallback(
    () => setFilters(courseHelpers.DEFAULT_FILTERS),
    [],
  );
  const filtersAreDefault = useMemo(
    () => courseHelpers.isDefaultFilters(filters),
    [filters],
  );
  const filteredCourses = useMemo(
    () => courseHelpers.filterAndSortCourses(courses, filters),
    [courses, filters],
  );
  const availableDifficulties = useMemo(
    () => courseHelpers.getAvailableDifficulties(courses),
    [courses],
  );

  if (!isLoading && courses.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <CoursesNotFound
          t={t}
          placeholderImageUrl={placeholderImageUrl}
          title={<Typography className={classes.title}>{notFoundTitle}</Typography>}
          description={notFoundDescription}
          {...notFoundActionProps}
        />
      </Box>
    );
  }

  return (
    <>
      {courses.length > 0 && (
        <CoursesToolbar
          t={t}
          showVisibility={showVisibility}
          visibility={filters.visibility}
          onVisibilityChange={handleVisibilityChange}
          lang={filters.lang}
          onLangChange={handleLangChange}
          difficulty={filters.difficulty}
          onDifficultyChange={handleDifficultyChange}
          availableDifficulties={availableDifficulties}
          sort={filters.sort}
          onSortChange={handleSortChange}
          search={filters.search}
          onSearchChange={handleSearchChange}
          onClearFilters={handleClearFilters}
          clearFiltersDisabled={filtersAreDefault}
        />
      )}
      {courses.length > 0 && filteredCourses.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography>{t("no_results_found")}</Typography>
          <Button
            onClick={handleClearFilters}
            disabled={filtersAreDefault}
            sx={{ mt: 1 }}
          >
            {t("clear_filters")}
          </Button>
        </Box>
      ) : (
        <CourseList
          t={t}
          courses={courses.length ? filteredCourses : null}
          completions={completions}
          handleStartCourse={handleStartCourse}
          handleLeaveCourse={handleLeaveCourse}
          handleViewCourseTopic={handleViewCourseTopic}
          handleViewCourse={handleViewCourse}
          handleViewUserProfile={handleViewUserProfile}
          membersProps={membersProps}
          getMemberDetails={getMemberDetails}
          singleLineGrid={singleLineGrid}
          singleLineGridCols={singleLineGridCols}
          memberPlaceholderImageUrl={memberPlaceholderImageUrl}
          getCourseSections={getCourseSections}
          getCourseMember={getCourseMember}
          getCourseMembers={getCourseMembers}
          memberId={memberId}
          username={username}
          handleDuplicateCourse={handleDuplicateCourse}
          cardTitleComponent='h2'
          email={email}
          isTeacher={isTeacher}
          handleDeleteCourse={handleDeleteCourse}
        />
      )}
    </>
  );
}

export default function Courses({
  t = (text) => text,
  role,
  learnCourses = [],
  teachCourses = [],
  completions = [],
  isLoading,
  placeholderImageUrl,
  singleLineGrid = false,
  singleLineGridCols = 4,
  handleStartCourse,
  handleLeaveCourse,
  handleViewCourse,
  handleViewCourseTopic,
  handleViewUserProfile,
  handleSearch,
  handleCreateCourse,
  membersProps,
  getMemberDetails,
  memberPlaceholderImageUrl,
  getCourseSections,
  getCourseMember,
  getCourseMembers,
  username,
  memberId,
  handleDuplicateCourse,
  email,
  isTeacher,
  handleDeleteCourse,
}) {
  const { classes } = useStyles();

  const tabs = [
    {
      label: `${t("created")} ${isLoading ? "" : `(${teachCourses.length})`}`,
      id: "Created",
    },
    {
      label: `${t("joined")} ${isLoading ? "" : `(${learnCourses.length})`}`,
      id: "Joined",
    },
  ];

  let initialTabValue;
  if (window.location.hash) {
    const selectedTab = window.location.hash;
    initialTabValue = tabs.findIndex(
      (tab) => tab.id === selectedTab.replace("#", ""),
    );
  } else if (role === "teach") {
    initialTabValue = 0; // Set initial tab to "Created"
  } else {
    initialTabValue =
      learnCourses.length > 0 && teachCourses.length === 0 ? 1 : 0;
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <Typography variant='h5' component='h1'>
            {t(`courses`)}
          </Typography>
        </div>
        {handleSearch && (
          <Tooltip title={t("find_course")}>
            <IconButton
              onClick={handleSearch}
              aria-label={t("find_course")}
              color='inherit'
              data-cy='search-course-fab'
              size='large'
              role='link'
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <ResponsiveTabs
        t={t}
        tabs={tabs}
        centered
        sticky
        stickySx={stickyBarBleed}
        initialValue={initialTabValue}
      >
        <Box sx={{
          py: 1
        }}>
          <CoursesTabPanel
            t={t}
            classes={classes}
            courses={teachCourses}
            isLoading={isLoading}
            showVisibility
            notFoundTitle={t("teach_courses_title")}
            notFoundDescription={t("teach_courses_desc")}
            notFoundActionProps={{ handleCreateCourse }}
            placeholderImageUrl={placeholderImageUrl}
            completions={completions}
            handleStartCourse={handleStartCourse}
            handleLeaveCourse={handleLeaveCourse}
            handleViewCourse={handleViewCourse}
            handleViewCourseTopic={handleViewCourseTopic}
            handleViewUserProfile={handleViewUserProfile}
            membersProps={membersProps}
            getMemberDetails={getMemberDetails}
            singleLineGrid={singleLineGrid}
            singleLineGridCols={singleLineGridCols}
            memberPlaceholderImageUrl={memberPlaceholderImageUrl}
            getCourseSections={getCourseSections}
            getCourseMember={getCourseMember}
            getCourseMembers={getCourseMembers}
            memberId={memberId}
            username={username}
            handleDuplicateCourse={handleDuplicateCourse}
            email={email}
            isTeacher={isTeacher}
            handleDeleteCourse={handleDeleteCourse}
          />
        </Box>
        <Box sx={{
          py: 1
        }}>
          <CoursesTabPanel
            t={t}
            classes={classes}
            courses={learnCourses}
            isLoading={isLoading}
            showVisibility={false}
            notFoundTitle={t("learn_courses_title")}
            notFoundDescription={t("learn_courses_desc")}
            notFoundActionProps={{ handleSearch }}
            placeholderImageUrl={placeholderImageUrl}
            completions={completions}
            handleStartCourse={handleStartCourse}
            handleLeaveCourse={handleLeaveCourse}
            handleViewCourse={handleViewCourse}
            handleViewCourseTopic={handleViewCourseTopic}
            handleViewUserProfile={handleViewUserProfile}
            membersProps={membersProps}
            getMemberDetails={getMemberDetails}
            singleLineGrid={singleLineGrid}
            singleLineGridCols={singleLineGridCols}
            memberPlaceholderImageUrl={memberPlaceholderImageUrl}
            getCourseSections={getCourseSections}
            getCourseMember={getCourseMember}
            getCourseMembers={getCourseMembers}
            memberId={memberId}
            username={username}
            handleDuplicateCourse={handleDuplicateCourse}
            isTeacher={isTeacher}
            handleDeleteCourse={handleDeleteCourse}
          />
        </Box>
      </ResponsiveTabs>
    </div>
  );
}
